import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { RoutePage } from './app/RoutePage';
import { fromSlug, MonthCellsProvider, MonthView, toSlug, useCurrentMonth, monthLabel as ymLabel, today as ymToday } from './calendar';
import { DotSpinner } from './components';
import { Layout } from './layout';
import { RequireConfigured } from './settings';

const ReportView = lazy(() => import('./report').then((m) => ({ default: m.ReportView })));
const TeamView = lazy(() => import('./team').then((m) => ({ default: m.TeamView })));
const SettingsView = lazy(() => import('./settings').then((m) => ({ default: m.SettingsView })));

const RouteFallback: React.FC<{ label: string }> = ({ label }) => (
    <div
        style={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <DotSpinner label={label} />
    </div>
);

const CalendarRoute: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const parsed = slug ? fromSlug(slug) : null;

    if (!parsed) {
        return <Navigate to={`/calendar/${toSlug(ymToday())}`} replace />;
    }
    const canonical = toSlug(parsed);
    if (canonical !== slug) {
        return <Navigate to={`/calendar/${canonical}`} replace />;
    }
    return <CalendarContent />;
};

const CalendarContent: React.FC = () => {
    const { current } = useCurrentMonth();
    const label = ymLabel(current);
    return (
        <RoutePage title={`${label.month} ${label.year}`}>
            <RequireConfigured>
                <MonthCellsProvider>
                    <MonthView />
                </MonthCellsProvider>
            </RequireConfigured>
        </RoutePage>
    );
};

const ReportRoute: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const parsed = slug ? fromSlug(slug) : null;

    if (!parsed) {
        return <Navigate to={`/report/${toSlug(ymToday())}`} replace />;
    }
    const canonical = toSlug(parsed);
    if (canonical !== slug) {
        return <Navigate to={`/report/${canonical}`} replace />;
    }
    return (
        <RoutePage title="Monthly Report">
            <RequireConfigured>
                <Suspense fallback={<RouteFallback label="Loading report" />}>
                    <ReportView />
                </Suspense>
            </RequireConfigured>
        </RoutePage>
    );
};

const TeamRoute: React.FC = () => (
    <RoutePage title="Team">
        <RequireConfigured>
            <Suspense fallback={<RouteFallback label="Loading team" />}>
                <TeamView />
            </Suspense>
        </RequireConfigured>
    </RoutePage>
);

const SettingsRoute: React.FC = () => (
    <RoutePage title="Settings">
        <Suspense fallback={<RouteFallback label="Loading settings" />}>
            <SettingsView />
        </Suspense>
    </RoutePage>
);

const RootRedirect: React.FC = () => <Navigate to={`/calendar/${toSlug(ymToday())}`} replace />;
const BareReportRedirect: React.FC = () => <Navigate to={`/report/${toSlug(ymToday())}`} replace />;

export const App: React.FC = () => {
    return (
        <Routes>
            <Route element={<Layout />}>
                <Route index element={<RootRedirect />} />
                <Route path="calendar/:slug" element={<CalendarRoute />} />
                <Route path="report" element={<BareReportRedirect />} />
                <Route path="report/:slug" element={<ReportRoute />} />
                <Route path="team" element={<TeamRoute />} />
                <Route path="settings" element={<SettingsRoute />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
        </Routes>
    );
};
